import { Box, MenuItem, Paper, Typography } from '@mui/material';
import type { Editor } from '@tiptap/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { borderRadius, boxShadows, palette, spaces } from '../../../assets/styles/themes/tokens';
import type { Command, MenuOption } from '../../../utils/tipTapEditor/SlashCommandExtension';

interface MenuListProps {
	editor: Editor;
	items: MenuOption[];
	command: (command: Command, callback?: () => void) => void;
}

export const MenuList = React.forwardRef((props: MenuListProps, ref) => {
	const scrollContainer = useRef<HTMLDivElement>(null);
	const activeItem = useRef<HTMLButtonElement>(null);
	const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
	const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		setSelectedGroupIndex(0);
		setSelectedCommandIndex(0);
	}, [props.items]);

	const selectItem = useCallback(
		(groupIndex: number, commandIndex: number) => {
			const command = props.items[groupIndex].commands[commandIndex];
			props.command(command);
		},
		[props]
	);

	React.useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
			if (event.key === 'ArrowDown') {
				if (!props.items.length) {
					return false;
				}

				const commands = props.items[selectedGroupIndex].commands;

				let newCommandIndex = selectedCommandIndex + 1;
				let newGroupIndex = selectedGroupIndex;

				if (commands.length - 1 < newCommandIndex) {
					newCommandIndex = 0;
					newGroupIndex = selectedGroupIndex + 1;
				}

				if (props.items.length - 1 < newGroupIndex) {
					newGroupIndex = 0;
				}

				setSelectedCommandIndex(newCommandIndex);
				setSelectedGroupIndex(newGroupIndex);

				return true;
			}

			if (event.key === 'ArrowUp') {
				if (!props.items.length) {
					return false;
				}

				let newCommandIndex = selectedCommandIndex - 1;
				let newGroupIndex = selectedGroupIndex;

				if (newCommandIndex < 0) {
					newGroupIndex = selectedGroupIndex - 1;
					newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0;
				}

				if (newGroupIndex < 0) {
					newGroupIndex = props.items.length - 1;
					newCommandIndex = props.items[newGroupIndex].commands.length - 1;
				}

				setSelectedCommandIndex(newCommandIndex);
				setSelectedGroupIndex(newGroupIndex);

				return true;
			}

			if (event.key === 'Enter') {
				if (!props.items.length || selectedGroupIndex === -1 || selectedCommandIndex === -1) {
					return false;
				}

				selectItem(selectedGroupIndex, selectedCommandIndex);

				return true;
			}

			return false;
		},
	}));

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (activeItem.current && scrollContainer.current) {
			const offsetTop = activeItem.current.offsetTop;
			const offsetHeight = activeItem.current.offsetHeight;

			scrollContainer.current.scrollTop = offsetTop - offsetHeight;
		}
	}, [selectedCommandIndex, selectedGroupIndex]);

	const createCommandClickHandler = useCallback(
		(groupIndex: number, commandIndex: number) => {
			return () => {
				selectItem(groupIndex, commandIndex);
			};
		},
		[selectItem]
	);

	if (!props.items.length) {
		return null;
	}

	return (
		<Paper
			ref={scrollContainer}
			sx={{
				width: 'fit-content',
				boxShadow: boxShadows.elevation8,
				borderRadius: borderRadius.xs,
				padding: spaces.xs,
			}}
		>
			<Box display="grid" gridTemplateColumns="1fr" gap={spaces.xxxs} data-testid="menu-list">
				{props.items.map((group, groupIndex) => (
					<React.Fragment key={`${group.title}-wrapper`}>
						<Typography
							variant="body1"
							sx={{
								color: palette.text.secondary,
							}}
						>
							{group.title}
						</Typography>
						{group.commands.map((command, commandIndex) => {
							const IconComponent = command.icon;
							return (
								<MenuItem
									key={`${command.label}`}
									selected={selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex}
									onClick={createCommandClickHandler(groupIndex, commandIndex)}
									sx={{
										display: 'flex',
										gap: spaces.xxs,
										borderRadius: borderRadius.sm,
										marginLeft: spaces.xxxs,
									}}
								>
									<IconComponent />
									<Typography sx={{ fontWeight: 400, color: palette.text.primary }}>
										{command.label}
									</Typography>
								</MenuItem>
							);
						})}
					</React.Fragment>
				))}
			</Box>
		</Paper>
	);
});

export default MenuList;
